// On-line membership application. Only rendered while 後台 → 網站設定 →
// 會員制度與會費 → 「開放線上入會申請」is on; otherwise MembershipInvite is shown.

import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { applicationProcess } from "@/mocks/membershipData";
import Icon from "@/components/feature/Icon";
import { formatFee, useSiteSettings } from "@/lib/siteSettings";
import MembershipFaq from "./MembershipFaq";

export default function MembershipApplicationForm() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [membershipType, setMembershipType] = useState("personal");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { settings } = useSiteSettings();
  const fees = settings.membership.fees;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    const form = e.currentTarget;
    const formData = new FormData(form);

    const payload = {
      membership_type: membershipType,
      name: String(formData.get("name") || ""),
      email: String(formData.get("email") || ""),
      organization: String(formData.get("organization") || ""),
      title: String(formData.get("title") || ""),
      phone: String(formData.get("phone") || ""),
      industry: String(formData.get("industry") || ""),
      reason: String(formData.get("reason") || ""),
      volunteer: formData.get("volunteer") === "on",
      newsletter: formData.get("newsletter") === "on",
      status: "pending",
    };

    try {
      const { error } = await supabase
        .from("membership_applications")
        .insert([payload]);

      if (error) {
        throw error;
      }

      setSubmitted(true);
      form.reset();
    } catch (err) {
      // 42501 = the database refused the insert: applications were closed
      // (invitation-only) after this page was opened.
      const code = (err as { code?: string } | null)?.code;
      const message =
        code === "42501"
          ? "目前未開放線上入會申請，請直接來信秘書處洽詢。"
          : err instanceof Error && err.message
            ? err.message
            : "提交失敗，請稍後再試。";
      setSubmitError(`提交失敗：${message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section id="join" className="scroll-mt-20 py-24 md:py-32 bg-navy-950">
        <div className="px-6 md:px-10 lg:px-16">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-6">
              <Icon className="ri-check-line text-4xl text-white" />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white mb-4">
              申請已提交
            </h2>
            <p className="text-silver-300 text-base leading-relaxed mb-8">
              感謝您的申請！秘書處將於 5 個工作天內完成資格審核，並以 Email 通知您審核結果與後續繳費方式。
            </p>
            <button
              onClick={() => setSubmitted(false)}
              className="inline-flex items-center gap-2 bg-white text-navy-950 px-7 py-3.5 rounded-full text-sm font-medium hover:bg-silver-100 transition-colors whitespace-nowrap"
            >
              再次申請
              <Icon className="ri-arrow-right-line" />
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="join" className="scroll-mt-20 py-24 md:py-32 bg-white" data-testid="membership-application">
      <div className="px-6 md:px-10 lg:px-16">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
            <p className="text-sm font-medium tracking-[0.2em] text-navy-700 uppercase mb-3">
              Application
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold text-navy-950 leading-tight mb-5">
              入會申請
            </h2>
            <p className="text-silver-500 text-base md:text-lg leading-relaxed">
              請選擇會員類型並填寫以下資料，我們將盡快為您辦理入會手續。
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
            {/* Left - Process */}
            <div className="lg:col-span-2">
              <h3 className="text-lg font-semibold text-navy-950 mb-8">申請流程</h3>
              <div className="space-y-8">
                {applicationProcess.map((step, idx) => (
                  <div key={step.step} className="flex gap-5">
                    <div className="flex flex-col items-center">
                      <div className="w-10 h-10 rounded-full bg-navy-950 text-white flex items-center justify-center text-sm font-semibold shrink-0">
                        {step.step}
                      </div>
                      {idx < applicationProcess.length - 1 && (
                        <div className="w-px flex-1 bg-silver-200 my-2" />
                      )}
                    </div>
                    <div className="pb-2">
                      <h4 className="text-base font-semibold text-navy-950 mb-1">{step.title}</h4>
                      <p className="text-sm text-silver-500 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <MembershipFaq className="mt-14" />
            </div>

            {/* Right - Form */}
            <div className="lg:col-span-3">
              <div className="bg-silver-50 rounded-3xl p-7 md:p-10 border border-silver-200">
                <form
                  onSubmit={handleSubmit}
                  className="space-y-6"
                >
                  {/* Membership type */}
                  <div>
                    <label className="block text-sm font-medium text-navy-950 mb-3">
                      會員類型 <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { value: "personal", label: "個人會員", fee: `NT$ ${formatFee(fees.personal)}/年` },
                        { value: "group", label: "團體會員", fee: `NT$ ${formatFee(fees.group)}/年` },
                        { value: "corporate", label: "企業會員", fee: `NT$ ${formatFee(fees.corporate)}/年` },
                      ].map((t) => (
                        <button
                          key={t.value}
                          type="button"
                          onClick={() => setMembershipType(t.value)}
                          className={`p-4 rounded-2xl border text-center transition-all ${
                            membershipType === t.value
                              ? "bg-navy-950 text-white border-navy-950"
                              : "bg-white border-silver-200 hover:border-navy-300"
                          }`}
                        >
                          <div className="text-sm font-medium mb-1">{t.label}</div>
                          <div
                            className={`text-xs ${
                              membershipType === t.value ? "text-silver-300" : "text-silver-500"
                            }`}
                          >
                            {t.fee}
                          </div>
                        </button>
                      ))}
                    </div>
                    <input type="hidden" name="membership_type" value={membershipType} />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-navy-950 mb-2">
                        姓名 / 單位聯絡人 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        className="w-full px-4 py-3 bg-white border border-silver-200 rounded-xl text-sm text-navy-950 placeholder:text-silver-400 focus:outline-none focus:border-navy-700 transition-colors"
                        placeholder="請輸入姓名"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-navy-950 mb-2">
                        單位 / 組織名稱
                      </label>
                      <input
                        type="text"
                        name="organization"
                        className="w-full px-4 py-3 bg-white border border-silver-200 rounded-xl text-sm text-navy-950 placeholder:text-silver-400 focus:outline-none focus:border-navy-700 transition-colors"
                        placeholder="個人會員可填寫服務單位"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-navy-950 mb-2">
                        職稱
                      </label>
                      <input
                        type="text"
                        name="title"
                        className="w-full px-4 py-3 bg-white border border-silver-200 rounded-xl text-sm text-navy-950 placeholder:text-silver-400 focus:outline-none focus:border-navy-700 transition-colors"
                        placeholder="例如：經理、教授、工程師"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-navy-950 mb-2">
                        電話
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        className="w-full px-4 py-3 bg-white border border-silver-200 rounded-xl text-sm text-navy-950 placeholder:text-silver-400 focus:outline-none focus:border-navy-700 transition-colors"
                        placeholder="09XX-XXX-XXX"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-navy-950 mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 bg-white border border-silver-200 rounded-xl text-sm text-navy-950 placeholder:text-silver-400 focus:outline-none focus:border-navy-700 transition-colors"
                      placeholder="example@company.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-navy-950 mb-2">
                      產業類別 / 專業領域
                    </label>
                    <input
                      type="text"
                      name="industry"
                      className="w-full px-4 py-3 bg-white border border-silver-200 rounded-xl text-sm text-navy-950 placeholder:text-silver-400 focus:outline-none focus:border-navy-700 transition-colors"
                      placeholder="例如：資訊科技、金融服務、教育、製造業"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-navy-950 mb-2">
                      加入原因 / 期望
                    </label>
                    <textarea
                      name="reason"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 bg-white border border-silver-200 rounded-xl text-sm text-navy-950 placeholder:text-silver-400 focus:outline-none focus:border-navy-700 transition-colors resize-none"
                      placeholder="簡述您希望加入 TAIAA 的原因，以及期望獲得的資源或參與的活動類型（最多 500 字）"
                    />
                    <p className="text-xs text-silver-400 mt-1 text-right">最多 500 字</p>
                  </div>

                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      name="volunteer"
                      id="volunteer"
                      className="mt-0.5 w-4 h-4 rounded border-silver-300 text-navy-950 focus:ring-navy-700"
                    />
                    <label htmlFor="volunteer" className="text-sm text-silver-500 leading-relaxed">
                      我願意參與協會志工活動或活動支援
                    </label>
                  </div>

                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      name="newsletter"
                      id="newsletter"
                      className="mt-0.5 w-4 h-4 rounded border-silver-300 text-navy-950 focus:ring-navy-700"
                    />
                    <label htmlFor="newsletter" className="text-sm text-silver-500 leading-relaxed">
                      我願意訂閱 TAIAA 電子報，接收最新活動與產業資訊
                    </label>
                  </div>

                  {submitError && (
                    <p className="text-sm text-red-600 flex items-center gap-2" role="alert">
                      <Icon className="ri-error-warning-line" />
                      {submitError}
                    </p>
                  )}

                  <div className="pt-4">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full inline-flex items-center justify-center gap-2 bg-navy-950 text-white px-8 py-4 rounded-full text-sm font-medium hover:bg-navy-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {submitting ? (
                        <>
                          <Icon className="ri-loader-4-line animate-spin" />
                          提交中...
                        </>
                      ) : (
                        <>
                          提交入會申請
                          <Icon className="ri-arrow-right-line" />
                        </>
                      )}
                    </button>
                    <p className="text-center text-silver-400 text-xs mt-4">
                      提交即表示您同意協會之個資使用與章程規範。
                    </p>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}